import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useToast } from '../context/ToastContext';

export interface RNRazmerLot {
  serial: string;
  deadline: string;
  quantity: number;
}

export interface RNRazmerLotSuggestion {
  itemId: string;
  warehouseId: string;
  quantity: number;
  lots: RNRazmerLot[];
  shortfall: number;
  availableLots?: RNRazmerLot[];
  lotError?: { messageKey?: string; message?: string };
}

export type RNRazmerLotMap = Record<string, RNRazmerLotSuggestion>;

/**
 * Read-only FEFO/FIFO lot suggestion for the RN Размерување rows. Never saved by the client.
 * `refreshKey` is any value that changes after the RN is saved or a 6421/6423 document
 * action ran (the RN doc object is replaced by the owner in both cases).
 */
export interface RNRazmerLotPreviewRow {
  rowKey: string;
  sourceRowKey?: string;
  quantity: string;
  serial?: string;
  deadline?: string;
}

export const useRNRazmerLots = (
  rnId: string | undefined,
  refreshKey: unknown,
  previewRows: RNRazmerLotPreviewRow[] = [],
): RNRazmerLotMap => {
  const { showToast } = useToast();
  const [lots, setLots] = useState<RNRazmerLotMap>({});
  const requestVersionRef = useRef(0);
  const previewSignature = useMemo(() => JSON.stringify(previewRows), [previewRows]);

  useEffect(() => {
    if (!rnId) {
      setLots({});
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    const requestVersion = ++requestVersionRef.current;
    const apiUrl = process.env.REACT_APP_API_URL ?? '';
    axios
      .get<{ rows?: RNRazmerLotMap }>(`${apiUrl}/api/rn-work-orders/${rnId}/razmer-lots`, {
        headers: { 'x-auth-token': token },
      })
      .then((res) => {
        if (!cancelled && requestVersion === requestVersionRef.current) setLots(res.data?.rows || {});
      })
      .catch((err: any) => {
        if (cancelled || requestVersion !== requestVersionRef.current) return;
        setLots({});
        showToast(err?.response?.data?.message || 'Error', 'error');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rnId, refreshKey]);

  useEffect(() => {
    if (!rnId) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const rows = JSON.parse(previewSignature) as RNRazmerLotPreviewRow[];
    if (!rows.length) return;
    let cancelled = false;
    const timeout = window.setTimeout(() => {
      const requestVersion = ++requestVersionRef.current;
      const apiUrl = process.env.REACT_APP_API_URL ?? '';
      axios.post<{ rows?: RNRazmerLotMap }>(`${apiUrl}/api/rn-work-orders/${rnId}/razmer-lots/preview`, {
        rows: rows.map((row) => ({ rowKey: row.rowKey, sourceRowKey: row.sourceRowKey || row.rowKey, quantity: row.quantity, serial: row.serial || '', deadline: row.deadline || '' })),
      }, { headers: { 'x-auth-token': token } })
        .then((res) => { if (!cancelled && requestVersion === requestVersionRef.current) setLots(res.data?.rows || {}); })
        .catch((err: any) => { if (!cancelled) showToast(err?.response?.data?.message || 'Error', 'error'); });
    }, 400);
    return () => { cancelled = true; window.clearTimeout(timeout); };
  }, [rnId, previewSignature, showToast]);

  return lots;
};
